// src/tailwind-resolver.ts
// Platform-neutral Tailwind className → ResolvedStyles converter.
// Outputs CSS-standard values (px numbers, hex colors) that any renderer can consume.

import type { ResolvedStyles } from "@cogo/types";

// ── Tailwind Spacing Scale ──
const SPACING: Record<string, number> = {
  "0": 0,
  "0.5": 2,
  "1": 4,
  "1.5": 6,
  "2": 8,
  "2.5": 10,
  "3": 12,
  "3.5": 14,
  "4": 16,
  "5": 20,
  "6": 24,
  "7": 28,
  "8": 32,
  "9": 36,
  "10": 40,
  "11": 44,
  "12": 48,
  "14": 56,
  "16": 64,
  "20": 80,
  "24": 96,
  "28": 112,
  "32": 128,
  "36": 144,
  "40": 160,
  "44": 176,
  "48": 192,
  "52": 208,
  "56": 224,
  "60": 240,
  "64": 256,
  "72": 288,
  "80": 320,
  "96": 384,
};

// ── Tailwind Color Palette ──
const COLORS: Record<string, string> = {
  transparent: "transparent",
  current: "currentColor",
  black: "#000000",
  white: "#ffffff",
  // Gray
  "gray-50": "#f9fafb",
  "gray-100": "#f3f4f6",
  "gray-200": "#e5e7eb",
  "gray-300": "#d1d5db",
  "gray-400": "#9ca3af",
  "gray-500": "#6b7280",
  "gray-600": "#4b5563",
  "gray-700": "#374151",
  "gray-800": "#1f2937",
  "gray-900": "#111827",
  "gray-950": "#030712",
  // Slate
  "slate-50": "#f8fafc",
  "slate-100": "#f1f5f9",
  "slate-200": "#e2e8f0",
  "slate-300": "#cbd5e1",
  "slate-400": "#94a3b8",
  "slate-500": "#64748b",
  "slate-600": "#475569",
  "slate-700": "#334155",
  "slate-800": "#1e293b",
  "slate-900": "#0f172a",
  "slate-950": "#020617",
  // Blue
  "blue-50": "#eff6ff",
  "blue-100": "#dbeafe",
  "blue-200": "#bfdbfe",
  "blue-300": "#93c5fd",
  "blue-400": "#60a5fa",
  "blue-500": "#3b82f6",
  "blue-600": "#2563eb",
  "blue-700": "#1d4ed8",
  "blue-800": "#1e40af",
  "blue-900": "#1e3a8a",
  "blue-950": "#172554",
  // Red
  "red-50": "#fef2f2",
  "red-100": "#fee2e2",
  "red-200": "#fecaca",
  "red-300": "#fca5a5",
  "red-400": "#f87171",
  "red-500": "#ef4444",
  "red-600": "#dc2626",
  "red-700": "#b91c1c",
  "red-800": "#991b1b",
  "red-900": "#7f1d1d",
  // Green
  "green-50": "#f0fdf4",
  "green-100": "#dcfce7",
  "green-200": "#bbf7d0",
  "green-300": "#86efac",
  "green-400": "#4ade80",
  "green-500": "#22c55e",
  "green-600": "#16a34a",
  "green-700": "#15803d",
  "green-800": "#166534",
  "green-900": "#14532d",
  // Yellow / Amber
  "yellow-50": "#fefce8",
  "yellow-100": "#fef9c3",
  "yellow-200": "#fef08a",
  "yellow-300": "#fde047",
  "yellow-400": "#facc15",
  "yellow-500": "#eab308",
  "amber-50": "#fffbeb",
  "amber-100": "#fef3c7",
  "amber-200": "#fde68a",
  "amber-300": "#fcd34d",
  "amber-400": "#fbbf24",
  "amber-500": "#f59e0b",
  "amber-600": "#d97706",
  // Purple
  "purple-50": "#faf5ff",
  "purple-100": "#f3e8ff",
  "purple-200": "#e9d5ff",
  "purple-300": "#d8b4fe",
  "purple-400": "#c084fc",
  "purple-500": "#a855f7",
  "purple-600": "#9333ea",
  "purple-700": "#7e22ce",
  // Indigo
  "indigo-50": "#eef2ff",
  "indigo-100": "#e0e7ff",
  "indigo-200": "#c7d2fe",
  "indigo-300": "#a5b4fc",
  "indigo-400": "#818cf8",
  "indigo-500": "#6366f1",
  "indigo-600": "#4f46e5",
  "indigo-700": "#4338ca",
  // Orange
  "orange-50": "#fff7ed",
  "orange-100": "#ffedd5",
  "orange-200": "#fed7aa",
  "orange-300": "#fdba74",
  "orange-400": "#fb923c",
  "orange-500": "#f97316",
  "orange-600": "#ea580c",
  // Pink
  "pink-50": "#fdf2f8",
  "pink-100": "#fce7f3",
  "pink-200": "#fbcfe8",
  "pink-300": "#f9a8d4",
  "pink-400": "#f472b6",
  "pink-500": "#ec4899",
  "pink-600": "#db2777",
  // Zinc
  "zinc-50": "#fafafa",
  "zinc-100": "#f4f4f5",
  "zinc-200": "#e4e4e7",
  "zinc-300": "#d4d4d8",
  "zinc-400": "#a1a1aa",
  "zinc-500": "#71717a",
  "zinc-600": "#52525b",
  "zinc-700": "#3f3f46",
  "zinc-800": "#27272a",
  "zinc-900": "#18181b",
  "zinc-950": "#09090b",
  // Neutral
  "neutral-50": "#fafafa",
  "neutral-100": "#f5f5f5",
  "neutral-200": "#e5e5e5",
  "neutral-300": "#d4d4d4",
  "neutral-400": "#a3a3a3",
  "neutral-500": "#737373",
  "neutral-600": "#525252",
  "neutral-700": "#404040",
  "neutral-800": "#262626",
  "neutral-900": "#171717",
  "neutral-950": "#0a0a0a",
  // Stone
  "stone-50": "#fafaf9",
  "stone-100": "#f5f5f4",
  "stone-200": "#e7e5e4",
  "stone-300": "#d6d3d1",
  "stone-400": "#a8a29e",
  "stone-500": "#78716c",
  "stone-600": "#57534e",
  "stone-700": "#44403c",
  "stone-800": "#292524",
  "stone-900": "#1c1917",
  // Teal
  "teal-50": "#f0fdfa",
  "teal-400": "#2dd4bf",
  "teal-500": "#14b8a6",
  "teal-600": "#0d9488",
  // Cyan
  "cyan-50": "#ecfeff",
  "cyan-400": "#22d3ee",
  "cyan-500": "#06b6d4",
  "cyan-600": "#0891b2",
  // Emerald
  "emerald-50": "#ecfdf5",
  "emerald-400": "#34d399",
  "emerald-500": "#10b981",
  "emerald-600": "#059669",
  // Rose
  "rose-50": "#fff1f2",
  "rose-400": "#fb7185",
  "rose-500": "#f43f5e",
  "rose-600": "#e11d48",
  // Violet
  "violet-50": "#f5f3ff",
  "violet-400": "#a78bfa",
  "violet-500": "#8b5cf6",
  "violet-600": "#7c3aed",
  // Fuchsia
  "fuchsia-50": "#fdf4ff",
  "fuchsia-400": "#e879f9",
  "fuchsia-500": "#d946ef",
  "fuchsia-600": "#c026d3",
  // Sky
  "sky-50": "#f0f9ff",
  "sky-400": "#38bdf8",
  "sky-500": "#0ea5e9",
  "sky-600": "#0284c7",
  // Lime
  "lime-50": "#f7fee7",
  "lime-400": "#a3e635",
  "lime-500": "#84cc16",
  "lime-600": "#65a30d",
};

// ── Theme tokens → CSS variable references ──
const THEME_TOKENS: Record<string, string> = {
  background: "var(--background)",
  foreground: "var(--foreground)",
  card: "var(--card)",
  "card-foreground": "var(--card-foreground)",
  popover: "var(--popover)",
  "popover-foreground": "var(--popover-foreground)",
  primary: "var(--primary)",
  "primary-foreground": "var(--primary-foreground)",
  secondary: "var(--secondary)",
  "secondary-foreground": "var(--secondary-foreground)",
  muted: "var(--muted)",
  "muted-foreground": "var(--muted-foreground)",
  accent: "var(--accent)",
  "accent-foreground": "var(--accent-foreground)",
  destructive: "var(--destructive)",
  "destructive-foreground": "var(--destructive-foreground)",
  border: "var(--border)",
  input: "var(--color-input)",
  ring: "var(--ring)",
};

// ── Font Size Map ──
const FONT_SIZE: Record<string, number> = {
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  "2xl": 24,
  "3xl": 30,
  "4xl": 36,
  "5xl": 48,
  "6xl": 60,
  "7xl": 72,
  "8xl": 96,
  "9xl": 128,
};

// ── Font Weight Map ──
const FONT_WEIGHT: Record<string, string> = {
  thin: "100",
  extralight: "200",
  light: "300",
  normal: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
  extrabold: "800",
  black: "900",
};

// ── Border Radius Map ──
const BORDER_RADIUS: Record<string, number> = {
  none: 0,
  sm: 2,
  DEFAULT: 4,
  md: 6,
  lg: 8,
  xl: 12,
  "2xl": 16,
  "3xl": 24,
  full: 9999,
};

// ── Shadow Presets (CSS box-shadow strings) ──
const SHADOWS: Record<string, string> = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  DEFAULT: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  none: "none",
};

// ── Line Height Map ──
const LINE_HEIGHT: Record<string, number | string> = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
  "3": 12,
  "4": 16,
  "5": 20,
  "6": 24,
  "7": 28,
  "8": 32,
  "9": 36,
  "10": 40,
};

// ── Letter Spacing Map ──
const LETTER_SPACING: Record<string, number> = {
  tighter: -0.8,
  tight: -0.4,
  normal: 0,
  wide: 0.4,
  wider: 0.8,
  widest: 1.6,
};

/**
 * Convert a Tailwind className string to platform-agnostic ResolvedStyles.
 * Only resolves static base classes; responsive (sm:, md:, lg:) and
 * state (hover:, focus:, active:) prefixes are skipped — they remain
 * in className for the Web renderer to handle natively.
 */
export function resolveTailwindToStyles(className: string): ResolvedStyles {
  if (!className || !className.trim()) return {};
  const result: ResolvedStyles = {};
  const tokens = className.trim().split(/\s+/);

  for (const token of tokens) {
    // Skip responsive prefixes (sm:, md:, lg:, xl:, 2xl:)
    if (/^(sm|md|lg|xl|2xl):/.test(token)) continue;
    // Skip state prefixes (hover:, focus:, active:, group-hover:, etc.)
    if (
      /^(hover|focus|active|focus-within|focus-visible|disabled|group-hover|peer-\w+):/.test(
        token,
      )
    )
      continue;
    // Skip dark mode prefix
    if (token.startsWith("dark:")) continue;

    const style = resolveToken(token);
    if (style) Object.assign(result, style);
  }

  return result;
}

function resolveToken(token: string): ResolvedStyles | null {
  // ── Padding ──
  if (/^p-(\d+\.?\d*)$/.test(token)) return { padding: sp(token.slice(2)) };
  if (/^px-(\d+\.?\d*)$/.test(token)) {
    const v = sp(token.slice(3));
    return { paddingLeft: v, paddingRight: v };
  }
  if (/^py-(\d+\.?\d*)$/.test(token)) {
    const v = sp(token.slice(3));
    return { paddingTop: v, paddingBottom: v };
  }
  if (/^pt-(\d+\.?\d*)$/.test(token)) return { paddingTop: sp(token.slice(3)) };
  if (/^pr-(\d+\.?\d*)$/.test(token))
    return { paddingRight: sp(token.slice(3)) };
  if (/^pb-(\d+\.?\d*)$/.test(token))
    return { paddingBottom: sp(token.slice(3)) };
  if (/^pl-(\d+\.?\d*)$/.test(token))
    return { paddingLeft: sp(token.slice(3)) };

  // ── Margin ──
  if (/^m-(\d+\.?\d*)$/.test(token)) return { margin: sp(token.slice(2)) };
  if (token === "mx-auto")
    return {
      marginLeft: "auto" as unknown as number,
      marginRight: "auto" as unknown as number,
    };
  if (/^mx-(\d+\.?\d*)$/.test(token)) {
    const v = sp(token.slice(3));
    return { marginLeft: v, marginRight: v };
  }
  if (/^my-(\d+\.?\d*)$/.test(token)) {
    const v = sp(token.slice(3));
    return { marginTop: v, marginBottom: v };
  }
  if (/^mt-(\d+\.?\d*)$/.test(token)) return { marginTop: sp(token.slice(3)) };
  if (/^mr-(\d+\.?\d*)$/.test(token))
    return { marginRight: sp(token.slice(3)) };
  if (/^mb-(\d+\.?\d*)$/.test(token))
    return { marginBottom: sp(token.slice(3)) };
  if (/^ml-(\d+\.?\d*)$/.test(token)) return { marginLeft: sp(token.slice(3)) };

  // ── Negative Margin ──
  if (/^-m-(\d+\.?\d*)$/.test(token)) return { margin: -sp(token.slice(3)) };
  if (/^-mx-(\d+\.?\d*)$/.test(token)) {
    const v = -sp(token.slice(4));
    return { marginLeft: v, marginRight: v };
  }
  if (/^-my-(\d+\.?\d*)$/.test(token)) {
    const v = -sp(token.slice(4));
    return { marginTop: v, marginBottom: v };
  }
  if (/^-mt-(\d+\.?\d*)$/.test(token))
    return { marginTop: -sp(token.slice(4)) };
  if (/^-mr-(\d+\.?\d*)$/.test(token))
    return { marginRight: -sp(token.slice(4)) };
  if (/^-mb-(\d+\.?\d*)$/.test(token))
    return { marginBottom: -sp(token.slice(4)) };
  if (/^-ml-(\d+\.?\d*)$/.test(token))
    return { marginLeft: -sp(token.slice(4)) };

  // ── Gap ──
  if (/^gap-(\d+\.?\d*)$/.test(token)) return { gap: sp(token.slice(4)) };
  if (/^gap-x-(\d+\.?\d*)$/.test(token))
    return { columnGap: sp(token.slice(6)) };
  if (/^gap-y-(\d+\.?\d*)$/.test(token)) return { rowGap: sp(token.slice(6)) };
  // Portable projection of Tailwind's direct-child spacing utilities.
  // Native renderers consume these as cross-axis child gaps; the web target
  // continues to emit Tailwind's direct-child margin selectors.
  if (/^space-x-(\d+\.?\d*)$/.test(token))
    return { columnGap: sp(token.slice(8)) };
  if (/^space-y-(\d+\.?\d*)$/.test(token))
    return { rowGap: sp(token.slice(8)) };

  // ── Width / Height ──
  if (token === "w-full") return { width: "100%" };
  if (token === "h-full") return { height: "100%" };
  if (token === "w-screen") return { width: "100vw" };
  if (token === "h-screen") return { height: "100vh" };
  if (token === "w-auto") return { width: "auto" };
  if (token === "h-auto") return { height: "auto" };
  if (/^w-(\d+\.?\d*)$/.test(token)) return { width: sp(token.slice(2)) };
  if (/^h-(\d+\.?\d*)$/.test(token)) return { height: sp(token.slice(2)) };
  // Fractional widths
  if (token === "w-1/2") return { width: "50%" };
  if (token === "w-1/3") return { width: "33.333333%" };
  if (token === "w-2/3") return { width: "66.666667%" };
  if (token === "w-1/4") return { width: "25%" };
  if (token === "w-3/4") return { width: "75%" };
  // Fit / min / max content sizing
  if (token === "w-fit") return { width: "fit-content" };
  if (token === "h-fit") return { height: "fit-content" };
  if (token === "w-min") return { width: "min-content" };
  if (token === "w-max") return { width: "max-content" };
  if (token === "h-min") return { height: "min-content" };
  if (token === "h-max") return { height: "max-content" };
  // Arbitrary values
  const minHMatch = token.match(/^min-h-\[(\d+)px\]$/);
  if (minHMatch) return { minHeight: parseInt(minHMatch[1]) };
  const maxWMatch = token.match(/^max-w-\[(\d+)px\]$/);
  if (maxWMatch) return { maxWidth: parseInt(maxWMatch[1]) };
  const minWMatch = token.match(/^min-w-\[(\d+)px\]$/);
  if (minWMatch) return { minWidth: parseInt(minWMatch[1]) };
  const maxHMatch = token.match(/^max-h-\[(\d+)px\]$/);
  if (maxHMatch) return { maxHeight: parseInt(maxHMatch[1]) };
  // max-w named sizes
  if (token === "max-w-sm") return { maxWidth: 384 };
  if (token === "max-w-md") return { maxWidth: 448 };
  if (token === "max-w-lg") return { maxWidth: 512 };
  if (token === "max-w-xl") return { maxWidth: 576 };
  if (token === "max-w-2xl") return { maxWidth: 672 };
  if (token === "max-w-3xl") return { maxWidth: 768 };
  if (token === "max-w-4xl") return { maxWidth: 896 };
  if (token === "max-w-5xl") return { maxWidth: 1024 };
  if (token === "max-w-6xl") return { maxWidth: 1152 };
  if (token === "max-w-7xl") return { maxWidth: 1280 };
  if (token === "max-w-full") return { maxWidth: "100%" };
  if (token === "max-w-none") return { maxWidth: "none" };

  // Arbitrary percent & calc heights/widths (JIT-immune Dynamic Inline-Style Fallback)
  const arbPctHeight = token.match(/^h-\[(\d+(?:\.\d+)?)%\]$/);
  if (arbPctHeight) return { height: `${arbPctHeight[1]}%` };
  const arbPctWidth = token.match(/^w-\[(\d+(?:\.\d+)?)%\]$/);
  if (arbPctWidth) return { width: `${arbPctWidth[1]}%` };
  const arbCalcHeight = token.match(/^h-\[calc\((.+)\)\]$/);
  if (arbCalcHeight) return { height: `calc(${arbCalcHeight[1]})` };
  const arbCalcWidth = token.match(/^w-\[calc\((.+)\)\]$/);
  if (arbCalcWidth) return { width: `calc(${arbCalcWidth[1]})` };

  // ── Flex Layout ──
  if (token === "flex") return { display: "flex" };
  if (token === "inline-flex") return { display: "inline-flex" };
  if (token === "grid") return { display: "grid" };
  if (token === "block") return { display: "block" };
  if (token === "inline-block") return { display: "inline-block" };
  if (token === "inline") return { display: "inline" };
  if (token === "flex-row") return { flexDirection: "row" };
  if (token === "flex-col") return { flexDirection: "column" };
  if (token === "flex-row-reverse") return { flexDirection: "row-reverse" };
  if (token === "flex-col-reverse") return { flexDirection: "column-reverse" };
  if (token === "flex-1") return { flex: 1 };
  if (token === "flex-auto") return { flex: 1 }; // simplified
  if (token === "flex-none") return { flex: 0 };
  if (token === "flex-wrap") return { flexWrap: "wrap" };
  if (token === "flex-nowrap") return { flexWrap: "nowrap" };
  if (token === "flex-wrap-reverse") return { flexWrap: "wrap-reverse" };
  if (token === "items-center") return { alignItems: "center" };
  if (token === "items-start") return { alignItems: "flex-start" };
  if (token === "items-end") return { alignItems: "flex-end" };
  if (token === "items-stretch") return { alignItems: "stretch" };
  if (token === "items-baseline") return { alignItems: "baseline" };
  if (token === "justify-center") return { justifyContent: "center" };
  if (token === "justify-start") return { justifyContent: "flex-start" };
  if (token === "justify-end") return { justifyContent: "flex-end" };
  if (token === "justify-between") return { justifyContent: "space-between" };
  if (token === "justify-around") return { justifyContent: "space-around" };
  if (token === "justify-evenly") return { justifyContent: "space-evenly" };
  if (token === "self-center") return { alignSelf: "center" };
  if (token === "self-start") return { alignSelf: "flex-start" };
  if (token === "self-end") return { alignSelf: "flex-end" };
  if (token === "self-stretch") return { alignSelf: "stretch" };
  if (token === "self-auto") return { alignSelf: "auto" };
  // Flex grow/shrink
  if (token === "grow") return { flexGrow: 1 };
  if (token === "grow-0") return { flexGrow: 0 };
  if (token === "shrink") return { flexShrink: 1 };
  if (token === "shrink-0") return { flexShrink: 0 };
  // Order
  const orderMatch = token.match(/^order-(\d+)$/);
  if (orderMatch) return { order: parseInt(orderMatch[1]) };
  if (token === "order-first") return { order: -9999 };
  if (token === "order-last") return { order: 9999 };
  if (token === "order-none") return { order: 0 };

  // ── Grid ──
  const gridColsMatch = token.match(/^grid-cols-(\d+)$/);
  if (gridColsMatch)
    return {
      gridTemplateColumns: `repeat(${gridColsMatch[1]}, minmax(0, 1fr))`,
    };

  // ── Border Radius ──
  if (token === "rounded") return { borderRadius: BORDER_RADIUS.DEFAULT };
  const roundedMatch = token.match(/^rounded-(sm|md|lg|xl|2xl|3xl|full|none)$/);
  if (roundedMatch) return { borderRadius: BORDER_RADIUS[roundedMatch[1]] };

  // ── Border Width ──
  if (token === "border") return { borderWidth: 1 };
  const borderWMatch = token.match(/^border-(\d+)$/);
  if (borderWMatch) return { borderWidth: parseInt(borderWMatch[1]) };
  if (token === "border-b") return { borderBottomWidth: 1 };
  if (token === "border-t") return { borderTopWidth: 1 };
  if (token === "border-l") return { borderLeftWidth: 1 };
  if (token === "border-r") return { borderRightWidth: 1 };
  const borderBW = token.match(/^border-b-(\d+)$/);
  if (borderBW) return { borderBottomWidth: parseInt(borderBW[1]) };
  const borderTW = token.match(/^border-t-(\d+)$/);
  if (borderTW) return { borderTopWidth: parseInt(borderTW[1]) };

  // ── Shadow ──
  if (token === "shadow") return { boxShadow: SHADOWS.DEFAULT };
  const shadowMatch = token.match(/^shadow-(sm|md|lg|xl|2xl|none)$/);
  if (shadowMatch) return { boxShadow: SHADOWS[shadowMatch[1]] };

  // ── Ring (approximated as box-shadow) ──
  if (token === "ring") return { boxShadow: "0 0 0 3px var(--ring)" };
  if (token === "ring-0") return { boxShadow: "0 0 0 0px transparent" };
  if (token === "ring-inset") return null; // modifier only, skip
  const ringMatch = token.match(/^ring-(\d+)$/);
  if (ringMatch) {
    return { boxShadow: `0 0 0 ${ringMatch[1]}px var(--ring)` };
  }

  // ── Aspect Ratio ──
  if (token === "aspect-square") return { aspectRatio: "1 / 1" };
  if (token === "aspect-video") return { aspectRatio: "16 / 9" };
  if (token === "aspect-auto") return { aspectRatio: "auto" };

  // ── Font Size ──
  const fontMatch = token.match(
    /^text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)$/,
  );
  if (fontMatch) return { fontSize: FONT_SIZE[fontMatch[1]] };

  // ── Text Alignment ──
  if (token === "text-center") return { textAlign: "center" };
  if (token === "text-right") return { textAlign: "right" };
  if (token === "text-left") return { textAlign: "left" };
  if (token === "text-justify") return { textAlign: "justify" };

  // ── Font Weight ──
  const weightMatch = token.match(
    /^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/,
  );
  if (weightMatch) return { fontWeight: FONT_WEIGHT[weightMatch[1]] };

  // ── Text Decoration ──
  if (token === "underline") return { textDecoration: "underline" };
  if (token === "line-through") return { textDecoration: "line-through" };
  if (token === "no-underline") return { textDecoration: "none" };

  // ── Text Transform ──
  if (token === "uppercase") return { textTransform: "uppercase" };
  if (token === "lowercase") return { textTransform: "lowercase" };
  if (token === "capitalize") return { textTransform: "capitalize" };
  if (token === "normal-case") return { textTransform: "none" };

  // ── Line Height (leading) ──
  const leadingMatch = token.match(/^leading-(.+)$/);
  if (leadingMatch && LINE_HEIGHT[leadingMatch[1]] != null) {
    return { lineHeight: LINE_HEIGHT[leadingMatch[1]] };
  }

  // ── Letter Spacing (tracking) ──
  const trackingMatch = token.match(/^tracking-(.+)$/);
  if (trackingMatch && LETTER_SPACING[trackingMatch[1]] != null) {
    return { letterSpacing: LETTER_SPACING[trackingMatch[1]] };
  }

  // ── Whitespace / Text overflow / Truncate ──
  if (token === "truncate")
    return {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };
  if (token === "text-ellipsis") return { textOverflow: "ellipsis" };
  if (token === "text-clip") return { textOverflow: "clip" };
  if (token === "whitespace-normal") return { whiteSpace: "normal" };
  if (token === "whitespace-nowrap") return { whiteSpace: "nowrap" };
  if (token === "whitespace-pre") return { whiteSpace: "pre" };
  if (token === "whitespace-pre-line") return { whiteSpace: "pre-line" };
  if (token === "whitespace-pre-wrap") return { whiteSpace: "pre-wrap" };
  if (token === "whitespace-break-spaces")
    return { whiteSpace: "break-spaces" };
  if (token === "break-words") return { wordBreak: "break-word" };
  if (token === "break-all") return { wordBreak: "break-all" };
  if (token === "break-normal") return { wordBreak: "normal" };

  // ── Display / Overflow ──
  if (token === "hidden") return { display: "none" };
  if (token === "overflow-hidden") return { overflow: "hidden" };
  if (token === "overflow-scroll") return { overflow: "scroll" };
  if (token === "overflow-auto") return { overflow: "auto" };
  if (token === "overflow-visible") return { overflow: "visible" };
  if (token === "overflow-x-hidden") return { overflowX: "hidden" };
  if (token === "overflow-x-auto") return { overflowX: "auto" };
  if (token === "overflow-x-scroll") return { overflowX: "scroll" };
  if (token === "overflow-y-hidden") return { overflowY: "hidden" };
  if (token === "overflow-y-auto") return { overflowY: "auto" };
  if (token === "overflow-y-scroll") return { overflowY: "scroll" };

  // ── Opacity ──
  const opacityMatch = token.match(/^opacity-(\d+)$/);
  if (opacityMatch) return { opacity: parseInt(opacityMatch[1]) / 100 };

  // ── Position ──
  if (token === "absolute") return { position: "absolute" };
  if (token === "relative") return { position: "relative" };
  if (token === "fixed") return { position: "fixed" };
  if (token === "sticky") return { position: "sticky" };
  if (token === "static") return { position: "static" };

  // ── Position values (inset) ──
  if (token === "inset-0") return { top: 0, right: 0, bottom: 0, left: 0 };
  const insetMatch = token.match(/^inset-(\d+\.?\d*)$/);
  if (insetMatch) {
    const v = sp(insetMatch[1]);
    return { top: v, right: v, bottom: v, left: v };
  }
  if (token === "inset-x-0") return { left: 0, right: 0 };
  if (token === "inset-y-0") return { top: 0, bottom: 0 };
  const insetXMatch = token.match(/^inset-x-(\d+\.?\d*)$/);
  if (insetXMatch) {
    const v = sp(insetXMatch[1]);
    return { left: v, right: v };
  }
  const insetYMatch = token.match(/^inset-y-(\d+\.?\d*)$/);
  if (insetYMatch) {
    const v = sp(insetYMatch[1]);
    return { top: v, bottom: v };
  }
  // Negative inset
  if (/^-inset-(\d+\.?\d*)$/.test(token)) {
    const v = -sp(token.slice(7));
    return { top: v, right: v, bottom: v, left: v };
  }
  if (/^-top-(\d+\.?\d*)$/.test(token)) return { top: -sp(token.slice(5)) };
  if (/^-right-(\d+\.?\d*)$/.test(token)) return { right: -sp(token.slice(7)) };
  if (/^-bottom-(\d+\.?\d*)$/.test(token))
    return { bottom: -sp(token.slice(8)) };
  if (/^-left-(\d+\.?\d*)$/.test(token)) return { left: -sp(token.slice(6)) };
  const topMatch = token.match(/^top-(\d+\.?\d*)$/);
  if (topMatch) return { top: sp(topMatch[1]) };
  const rightMatch = token.match(/^right-(\d+\.?\d*)$/);
  if (rightMatch) return { right: sp(rightMatch[1]) };
  const bottomMatch = token.match(/^bottom-(\d+\.?\d*)$/);
  if (bottomMatch) return { bottom: sp(bottomMatch[1]) };
  const leftMatch = token.match(/^left-(\d+\.?\d*)$/);
  if (leftMatch) return { left: sp(leftMatch[1]) };
  if (token === "top-0") return { top: 0 };
  if (token === "right-0") return { right: 0 };
  if (token === "bottom-0") return { bottom: 0 };
  if (token === "left-0") return { left: 0 };

  // ── Z-Index ──
  const zMatch = token.match(/^z-(\d+)$/);
  if (zMatch) return { zIndex: parseInt(zMatch[1]) };
  if (token === "z-auto") return { zIndex: 0 };

  // ── Background / Text / Border Colors ──
  const bgMatch = token.match(/^bg-(.+)$/);
  if (bgMatch) {
    const color = lookupColor(bgMatch[1]);
    if (color) return { backgroundColor: color };
  }

  // text-* can be font size or color — check font size first (handled above)
  const textColorMatch = token.match(/^text-(.+)$/);
  if (textColorMatch) {
    const name = textColorMatch[1];
    // Skip if it's a font size, alignment, or transform match
    if (
      FONT_SIZE[name] ||
      ["center", "right", "left", "justify"].includes(name)
    )
      return null;
    const color = lookupColor(name);
    if (color) return { color };
  }

  const borderColorMatch = token.match(/^border-(.+)$/);
  if (borderColorMatch) {
    const name = borderColorMatch[1];
    // Skip if it's a border width or side
    if (/^\d+$/.test(name) || ["b", "t", "l", "r"].includes(name)) return null;
    // Skip named border-radius values
    if (["sm", "md", "lg", "xl", "2xl", "3xl", "full", "none"].includes(name))
      return null;
    const color = lookupColor(name);
    if (color) return { borderColor: color };
  }

  // ── Transform ──
  // Scale
  const scaleMatch = token.match(/^scale-(\d+)$/);
  if (scaleMatch) return { transformScale: parseInt(scaleMatch[1]) / 100 };
  const scaleXMatch = token.match(/^scale-x-(\d+)$/);
  if (scaleXMatch) return { transformScaleX: parseInt(scaleXMatch[1]) / 100 };
  const scaleYMatch = token.match(/^scale-y-(\d+)$/);
  if (scaleYMatch) return { transformScaleY: parseInt(scaleYMatch[1]) / 100 };
  // Rotate
  const rotateMatch = token.match(/^rotate-(\d+)$/);
  if (rotateMatch) return { transformRotate: parseInt(rotateMatch[1]) };
  const negRotateMatch = token.match(/^-rotate-(\d+)$/);
  if (negRotateMatch) return { transformRotate: -parseInt(negRotateMatch[1]) };
  // Translate
  const translateXMatch = token.match(/^translate-x-(\d+\.?\d*)$/);
  if (translateXMatch) return { transformTranslateX: sp(translateXMatch[1]) };
  const negTranslateXMatch = token.match(/^-translate-x-(\d+\.?\d*)$/);
  if (negTranslateXMatch)
    return { transformTranslateX: -sp(negTranslateXMatch[1]) };
  const translateYMatch = token.match(/^translate-y-(\d+\.?\d*)$/);
  if (translateYMatch) return { transformTranslateY: sp(translateYMatch[1]) };
  const negTranslateYMatch = token.match(/^-translate-y-(\d+\.?\d*)$/);
  if (negTranslateYMatch)
    return { transformTranslateY: -sp(negTranslateYMatch[1]) };
  // Fractional translate
  if (token === "translate-x-1/2")
    return { transformTranslateX: "50%" as unknown as number };
  if (token === "-translate-x-1/2")
    return { transformTranslateX: "-50%" as unknown as number };
  if (token === "translate-y-1/2")
    return { transformTranslateY: "50%" as unknown as number };
  if (token === "-translate-y-1/2")
    return { transformTranslateY: "-50%" as unknown as number };
  if (token === "translate-x-full")
    return { transformTranslateX: "100%" as unknown as number };
  if (token === "-translate-x-full")
    return { transformTranslateX: "-100%" as unknown as number };
  if (token === "translate-y-full")
    return { transformTranslateY: "100%" as unknown as number };
  if (token === "-translate-y-full")
    return { transformTranslateY: "-100%" as unknown as number };
  // Skew
  const skewXMatch = token.match(/^skew-x-(\d+)$/);
  if (skewXMatch) return { transformSkewX: parseInt(skewXMatch[1]) };
  const negSkewXMatch = token.match(/^-skew-x-(\d+)$/);
  if (negSkewXMatch) return { transformSkewX: -parseInt(negSkewXMatch[1]) };
  const skewYMatch = token.match(/^skew-y-(\d+)$/);
  if (skewYMatch) return { transformSkewY: parseInt(skewYMatch[1]) };
  const negSkewYMatch = token.match(/^-skew-y-(\d+)$/);
  if (negSkewYMatch) return { transformSkewY: -parseInt(negSkewYMatch[1]) };
  // Transform Origin
  const originMatch = token.match(/^origin-(.+)$/);
  if (originMatch) {
    const originMap: Record<string, string> = {
      center: "center",
      top: "top",
      "top-right": "top right",
      right: "right",
      "bottom-right": "bottom right",
      bottom: "bottom",
      "bottom-left": "bottom left",
      left: "left",
      "top-left": "top left",
    };
    if (originMap[originMatch[1]])
      return { transformOrigin: originMap[originMatch[1]] };
  }

  // ── Transition ──
  if (token === "transition") return { transitionProperty: "all" };
  if (token === "transition-none") return { transitionProperty: "none" };
  if (token === "transition-all") return { transitionProperty: "all" };
  if (token === "transition-colors")
    return {
      transitionProperty:
        "color, background-color, border-color, text-decoration-color, fill, stroke",
    };
  if (token === "transition-opacity") return { transitionProperty: "opacity" };
  if (token === "transition-shadow")
    return { transitionProperty: "box-shadow" };
  if (token === "transition-transform")
    return { transitionProperty: "transform" };
  const durationMatch = token.match(/^duration-(\d+)$/);
  if (durationMatch) return { transitionDuration: parseInt(durationMatch[1]) };
  if (token === "ease-linear") return { transitionTimingFunction: "linear" };
  if (token === "ease-in")
    return { transitionTimingFunction: "cubic-bezier(0.4, 0, 1, 1)" };
  if (token === "ease-out")
    return { transitionTimingFunction: "cubic-bezier(0, 0, 0.2, 1)" };
  if (token === "ease-in-out")
    return { transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" };
  const delayMatch = token.match(/^delay-(\d+)$/);
  if (delayMatch) return { transitionDelay: parseInt(delayMatch[1]) };

  // ── Backdrop Filter ──
  const backdropBlurMap: Record<string, number> = {
    none: 0,
    sm: 4,
    DEFAULT: 8,
    md: 12,
    lg: 16,
    xl: 24,
    "2xl": 40,
    "3xl": 64,
  };
  const bdBlurMatch = token.match(/^backdrop-blur(?:-(.+))?$/);
  if (bdBlurMatch) {
    const key = bdBlurMatch[1] ?? "DEFAULT";
    if (backdropBlurMap[key] != null)
      return { backdropBlur: backdropBlurMap[key] };
  }
  const bdSatMatch = token.match(/^backdrop-saturate-(\d+)$/);
  if (bdSatMatch) return { backdropSaturate: parseInt(bdSatMatch[1]) / 100 };
  const bdBrightMatch = token.match(/^backdrop-brightness-(\d+)$/);
  if (bdBrightMatch)
    return { backdropBrightness: parseInt(bdBrightMatch[1]) / 100 };

  // ── Filter ──
  const filterBlurMap: Record<string, number> = {
    none: 0,
    sm: 4,
    DEFAULT: 8,
    md: 12,
    lg: 16,
    xl: 24,
    "2xl": 40,
    "3xl": 64,
  };
  const fBlurMatch = token.match(/^blur(?:-(.+))?$/);
  if (fBlurMatch) {
    const key = fBlurMatch[1] ?? "DEFAULT";
    if (filterBlurMap[key] != null) return { filterBlur: filterBlurMap[key] };
  }
  const brightnessMatch = token.match(/^brightness-(\d+)$/);
  if (brightnessMatch)
    return { filterBrightness: parseInt(brightnessMatch[1]) / 100 };
  const contrastMatch = token.match(/^contrast-(\d+)$/);
  if (contrastMatch)
    return { filterContrast: parseInt(contrastMatch[1]) / 100 };
  if (token === "grayscale") return { filterGrayscale: 1 };
  if (token === "grayscale-0") return { filterGrayscale: 0 };
  if (token === "invert") return { filterInvert: 1 };
  if (token === "invert-0") return { filterInvert: 0 };

  // ── Mix Blend Mode ──
  const blendMatch = token.match(/^mix-blend-(.+)$/);
  if (blendMatch) return { mixBlendMode: blendMatch[1] };

  // ── Text Wrap ──
  if (token === "text-balance") return { textWrap: "balance" };
  if (token === "text-pretty") return { textWrap: "pretty" };
  if (token === "text-nowrap") return { textWrap: "nowrap" };
  if (token === "text-wrap") return { textWrap: "wrap" };

  // ── Arbitrary values: w-[Npx], text-[Npx], p-[Npx], etc. ──
  const arbWidth = token.match(/^w-\[(\d+)px\]$/);
  if (arbWidth) return { width: parseInt(arbWidth[1]) };
  const arbHeight = token.match(/^h-\[(\d+)px\]$/);
  if (arbHeight) return { height: parseInt(arbHeight[1]) };
  const arbFontSize = token.match(/^text-\[(\d+)px\]$/);
  if (arbFontSize) return { fontSize: parseInt(arbFontSize[1]) };
  // Arbitrary spacing
  const arbPad = token.match(/^p-\[(\d+)px\]$/);
  if (arbPad) return { padding: parseInt(arbPad[1]) };
  const arbPx = token.match(/^px-\[(\d+)px\]$/);
  if (arbPx) {
    const v = parseInt(arbPx[1]);
    return { paddingLeft: v, paddingRight: v };
  }
  const arbPy = token.match(/^py-\[(\d+)px\]$/);
  if (arbPy) {
    const v = parseInt(arbPy[1]);
    return { paddingTop: v, paddingBottom: v };
  }
  const arbMar = token.match(/^m-\[(\d+)px\]$/);
  if (arbMar) return { margin: parseInt(arbMar[1]) };
  const arbMx = token.match(/^mx-\[(\d+)px\]$/);
  if (arbMx) {
    const v = parseInt(arbMx[1]);
    return { marginLeft: v, marginRight: v };
  }
  const arbMy = token.match(/^my-\[(\d+)px\]$/);
  if (arbMy) {
    const v = parseInt(arbMy[1]);
    return { marginTop: v, marginBottom: v };
  }
  const arbGap = token.match(/^gap-\[(\d+)px\]$/);
  if (arbGap) return { gap: parseInt(arbGap[1]) };
  // Arbitrary border radius
  const arbRounded = token.match(/^rounded-\[(\d+)px\]$/);
  if (arbRounded) return { borderRadius: parseInt(arbRounded[1]) };

  return null;
}

/** Resolve spacing scale value */
function sp(key: string): number {
  return SPACING[key] ?? parseInt(key) * 4;
}

/** Look up a Tailwind color name to hex or CSS variable */
function lookupColor(name: string): string | null {
  // Check Tailwind palette
  if (COLORS[name]) return COLORS[name];
  // Check theme tokens (e.g., "muted-foreground" → "var(--muted-foreground)")
  if (THEME_TOKENS[name]) return THEME_TOKENS[name];
  // Arbitrary hex color: [#rrggbb] or [#rgb]
  const arbHex = name.match(/^\[(#[0-9a-fA-F]{3,8})\]$/);
  if (arbHex) return arbHex[1];
  return null;
}
